import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';

import { ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import { MtnaPopoverConfig } from '@mtna/lib-ui';
import { Code, VariableSummary } from '@mtna/pojo-consumer-ui';

import { RdsChip } from '../chips/chips.component';
import { BaseFilter } from '../core/models/filter/base-filter.model';
import { RdsDateFilter } from '../core/models/filter/date-filter.model';
import { FilterByCodes } from '../core/models/filter/filter-by-codes.model';
import { FilterByDate } from '../core/models/filter/filter-by-date.model';
import { FilterByNumber } from '../core/models/filter/filter-by-number.model';
import { FilterByText } from '../core/models/filter/filter-by-text.model';
import { RdsFilterType } from '../core/models/filter/filter-type.model';
import { RdsFilterUtil } from '../core/utils/filter-util';
import { RdsNumericFilter } from '../numeric-filter/numeric-filter.model';
import { RdsTextFilter } from '../text-filter/text-filter.model';
import { RdsFilterDialogComponent, RdsFilterDialogData, RdsFilterDialogOutput } from './filter-dialog.component';
import { RdsVariableUtil } from '../core/utils/variable-util';
import { RdsNullFilter, RdsNullParameterOption, rdsNullParameterLabels, rdsNullParamters } from '../core/models/filter/null-filter.model';

@Component({
  selector: 'rds-filter-select',
  templateUrl: './filter-select.component.html',
  styleUrls: ['./filter-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class RdsFilterSelectComponent implements ControlValueAccessor, OnDestroy, OnInit {
  /** Popover configuration to describe the filter control */
  @Input() config: MtnaPopoverConfig = new MtnaPopoverConfig(
    'Filter',
    'Filters the data on any available variable. Some restrictions may apply for disclosure control purposes.',
    'after'
  );
  /**
   * NgStyle config for the dialog content's filter component, defaults to `{ height: '300px' }`
   * @example
   * { 'maxHeight': '300px' }
   */
  @Input() dialogFilterStyles: { [key: string]: string } = { height: '300px' };
  /** Optional filter dialog min-width */
  @Input() dialogMinWidth: string;

  /** Whether the control is disabled */
  @Input()
  get disabled() {
    return this._disabled;
  }
  set disabled(value: any) {
    const newValue = coerceBooleanProperty(value);
    if (newValue !== this.disabled) {
      this._disabled = newValue;
      this._changeDetectorRef.markForCheck();
    }
  }
  private _disabled = false;

  /** The selected filter and value for this control, if it exists */
  @Input()
  set filters(filters: Array<BaseFilter<VariableSummary>>) {
    this._filters = filters || [];
    this._changeDetectorRef.markForCheck();
  }
  get filters() {
    return this._filters;
  }
  private _filters: Array<BaseFilter<VariableSummary>> = [];

  /** Whether the use can make multiple variable selections. Default false */
  @Input() multiVariableSelect = false;
  /** Whether the user can make multiple code selections. Default false */
  @Input() multiSelect = false;

  /** Variables to choose from in the select. */
  @Input()
  get variables(): Array<VariableSummary> {
    return this._variables;
  }
  set variables(vars: Array<VariableSummary>) {
    this._variables = vars;
    this._varFilteredSummaries$.next(!vars ? [] : this.variables.slice());
  }
  private _variables: Array<VariableSummary>;

  /** Internal <mat-select> */
  @ViewChild(MatSelect) matSelect: MatSelect;

  /** Whether any search results were found */
  _noSearchResults = false;
  /** A subset of all the variables, filtered by the search text */
  _varFilteredSummaries$ = new ReplaySubject<VariableSummary[]>(1);

  private _onDestroy = new Subject<void>();

  _onTouched: () => any = () => {};
  private _controlValueAccessorChangeFn: (value: any) => void = () => {};

  constructor(
    private dialog: MatDialog,
    elementRef: ElementRef<HTMLElement>,
    @Optional() @Self() public ngControl: NgControl,
    private _changeDetectorRef: ChangeDetectorRef,
    private _focusMonitor: FocusMonitor
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this._focusMonitor.monitor(elementRef, true).subscribe((focusOrigin) => {
      if (!focusOrigin) {
        // When a focused element becomes disabled, the browser *immediately* fires a blur event.
        // Angular does not expect events to be raised during change detection, so any state change
        // (such as a form control's 'ng-touched') will cause a changed-after-checked error.
        // See https://github.com/angular/angular/issues/17793. To work around this, we defer
        // telling the form control it has been touched until the next tick.
        Promise.resolve().then(() => this._onTouched());
      }
    });
  }

  /**
   * When the mtna-select-search emits search text, filter the visible variables in the select.
   *
   * @param text search text to filter the variables
   */
  filterVariables(text: string) {
    if (!this.variables || !this.matSelect || !this.matSelect.panelOpen) {
      return;
    }
    if (!text) {
      this._varFilteredSummaries$.next(this.variables.slice());
      return;
    }
    // filter the variable summaries if search text exists in name or label
    this._varFilteredSummaries$.next(this.variables.filter((v) => RdsVariableUtil.filterVariable(text, v)));
  }

  /**
   * When the select panel is opened or closed, manipulate the options visible.
   * When the panel is opened, show the full list of variables.
   * When the panel is closed, set the selected value on the filtered subject so it will work with the MatSelect's internal selection model.
   *
   * @param opened Whether the panel is being opened
   */
  handleOpenedChange(opened: boolean) {
    if (opened) {
      // When the panel is opened, reset the filter to show all the variables
      this.filterVariables('');
    }
  }

  ngOnDestroy() {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngOnInit() {
    // Determine `_noSearchResults` each time the filtered variables changes
    this._varFilteredSummaries$.pipe(takeUntil(this._onDestroy)).subscribe((filteredVariables) => {
      const newValue = filteredVariables.length === 0;
      if (newValue !== this._noSearchResults) {
        this._noSearchResults = newValue;
        this._changeDetectorRef.markForCheck();
      }
    });
  }

  /**
   * Opens the filter dialog for adding filters.
   * Returns the applied filters, or null if cancelled
   * @param variableUri uri of the selected variable.
   */
  openFilterDialog(variableUri: string) {
    const filter = this.filters.find((f) => f.variable.uri === variableUri);
    const variable: VariableSummary = !!filter ? filter.variable : { ...this.variables.find((v) => v.uri === variableUri) };

    // Determine if there are already any applied filters
    let appliedFilters: Array<Code | RdsDateFilter | RdsNumericFilter | RdsTextFilter> = [];
    const filterType: RdsFilterType = RdsFilterUtil.determineType<VariableSummary>(variable);
    if (!!filter) {
      switch (filterType) {
        case 'CODE':
          appliedFilters = (filter as FilterByCodes).codes;
          break;
        case 'DATE':
          appliedFilters = (filter as FilterByDate<VariableSummary>).dateFilters;
          break;
        case 'NUMERIC':
          appliedFilters = (filter as FilterByNumber<VariableSummary>).numericFilters;
          break;
        case 'TEXT':
          appliedFilters = (filter as FilterByText<VariableSummary>).textFilters;
          break;
      }
    }

    const dialogRef = this.dialog.open(RdsFilterDialogComponent, {
      disableClose: true,
      data: new RdsFilterDialogData<VariableSummary>(
        variable,
        appliedFilters,
        filter?.nullFilter?.parameter?.type,
        this.multiSelect,
        this.dialogFilterStyles
      ),
      minWidth: this.dialogMinWidth,
    });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this._onDestroy))
      .subscribe((output: RdsFilterDialogOutput | null) => {
        const isPreviousFilter = !!filter;
        if (!!output) {
          const nullFilter = new RdsNullFilter(new RdsNullParameterOption(rdsNullParameterLabels[output.nullFilter], output.nullFilter));
          let newFilter:
            | FilterByCodes<VariableSummary>
            | FilterByDate<VariableSummary>
            | FilterByNumber<VariableSummary>
            | FilterByText<VariableSummary>;
          switch (filterType) {
            case 'CODE':
              newFilter = new FilterByCodes<VariableSummary>(variable, [...output.filters] as Code[], nullFilter);
              break;
            case 'DATE':
              newFilter = new FilterByDate<VariableSummary>(variable, [...output.filters] as RdsDateFilter[], nullFilter);
              break;
            case 'NUMERIC':
              newFilter = new FilterByNumber<VariableSummary>(variable, [...output.filters] as RdsNumericFilter[], nullFilter);
              break;
            case 'TEXT':
              newFilter = new FilterByText<VariableSummary>(variable, [...output.filters] as RdsTextFilter[], nullFilter);
              break;
          }

          // New fitlers were selected, so we want to add the filter to the list or replace the existing filter
          if (isPreviousFilter) {
            this.filters = this.filters.map((f) => (f.variable.uri === newFilter.variable.uri ? newFilter : f));
          } else {
            this.filters = this.multiVariableSelect ? [...this.filters, newFilter] : [newFilter];
          }
        } else if (!isPreviousFilter) {
          // If the dialog was cancelled, reset the selection model
          const option = this.matSelect.options.toArray().find((o) => !!o.value && o.value.uri === variableUri);
          option.deselect();
          this.matSelect._selectionModel.deselect(option);
          // reselect options for any previous filters
          this._resetSelectOptions();
        }

        this._changeDetectorRef.markForCheck();
        this._emitChangeEvent();
      });
  }

  /**
   * When an option is selected, open the dialog
   * @param v variable from the selected option
   */
  optionSelected(v: VariableSummary) {
    if (!v) {
      this.filters = null;
      this._emitChangeEvent();
    } else if (this.multiVariableSelect && !!this.filters && this.filters.some((f) => f.variable.uri === v.uri)) {
      // If we can select multiple variables, & the selected variable already exists in the filters,
      // then we want to remove it instead of opening the filter dialog
      this.matSelect._selectionModel.deselect(this.matSelect.options.toArray().find((o) => !!o.value && o.value.uri === v.uri));
      this.filters = this.filters.filter((f) => f.variable.uri !== v.uri);
      this._emitChangeEvent();
    } else {
      this.openFilterDialog(v.uri);
    }
  }

  removeFilter(baseFilter: BaseFilter<VariableSummary>, filterType: RdsFilterType, rdsChip: RdsChip) {
    // Existing filters
    let existingFilters: Array<Code | RdsDateFilter | RdsNumericFilter | RdsTextFilter> = [];
    // Updated filters with the filter represented by the chip, removed
    let updatedFilters: Array<Code | RdsDateFilter | RdsNumericFilter | RdsTextFilter> = [];
    let newFilter:
      | FilterByCodes<VariableSummary>
      | FilterByDate<VariableSummary>
      | FilterByNumber<VariableSummary>
      | FilterByText<VariableSummary>;
    switch (filterType) {
      case 'CODE':
        existingFilters = (baseFilter as FilterByCodes).codes;
        // Chip id is the code uri
        updatedFilters = existingFilters.filter((c: Code) => c.uri !== rdsChip.id);
        newFilter = { ...baseFilter, codes: updatedFilters as Code[] };
        break;
      case 'DATE':
        existingFilters = (baseFilter as FilterByDate<VariableSummary>).dateFilters;
        // Chip id is the operation.type + value.valueOf()
        updatedFilters = existingFilters.filter((d: RdsDateFilter) => `${d.operation.type}${d.value.valueOf()}` !== rdsChip.id);
        newFilter = { ...baseFilter, dateFilters: updatedFilters as RdsDateFilter[] };
        break;
      case 'NUMERIC':
        existingFilters = (baseFilter as FilterByNumber<VariableSummary>).numericFilters;
        // Chip id is the operation.type + value
        updatedFilters = existingFilters.filter((n: RdsNumericFilter) => `${n.operation.type}${n.value}` !== rdsChip.id);
        newFilter = { ...baseFilter, numericFilters: updatedFilters as RdsNumericFilter[] };
        break;
      case 'TEXT':
        existingFilters = (baseFilter as FilterByText<VariableSummary>).textFilters;
        // Chip id is the parameter.type + text
        updatedFilters = existingFilters.filter((n: RdsTextFilter) => `${n.parameter.type}${n.text}` !== rdsChip.id);
        newFilter = { ...baseFilter, textFilters: updatedFilters as RdsTextFilter[] };
        break;
    }

    // Handle the case that the chip represents a null filter
    if (rdsNullParamters.some((p) => p === rdsChip.id)) {
      const notApplied = new RdsNullFilter(new RdsNullParameterOption(rdsNullParameterLabels['NOT_APPLIED'], 'NOT_APPLIED'));
      newFilter = { ...newFilter, nullFilter: notApplied };
    }

    this.filters =
      updatedFilters.length > 0 || (!!newFilter.nullFilter?.parameter && newFilter.nullFilter.parameter.type !== 'NOT_APPLIED')
        ? // Update existing filters
          this.filters.map((f) => (f.variable.uri === baseFilter.variable.uri ? newFilter : f))
        : // If no filters left with this variable, remove variable
          this.filters.filter((f) => f.variable.uri !== baseFilter.variable.uri);

    this._emitChangeEvent();
  }

  registerOnChange(fn: any): void {
    this._controlValueAccessorChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(obj: any): void {
    this.filters = obj;
  }

  private _resetSelectOptions() {
    if (!!this.filters && !!this.filters.length) {
      const options = this.matSelect.options.toArray().filter((o) => !!o.value && this.filters.some((f) => f.variable.uri === o.value.uri));
      this.matSelect._selectionModel.select(...options);
    } else {
      // No previous filters, so just clear out the mat-select
      this.matSelect._selectionModel.clear();
    }
  }

  /**
   * Comparison function for use with `compareWith`
   */
  _compareVariable(v1: VariableSummary, v2: VariableSummary) {
    if (!v1 || !v2) {
      return false;
    }
    return v1.id === v2.id;
  }

  _emitChangeEvent() {
    this._controlValueAccessorChangeFn(this.filters);
  }

  _trackByVariable(index: number, variable: VariableSummary) {
    if (!variable) {
      return null;
    }
    return variable.id;
  }
}
